import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Download } from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";

import Panel from "../../../../components/PartnerDashboard/Panel";
import Skeleton from "../../../../components/PartnerDashboard/Skeleton";
import {
  Button,
  Chip,
  Chips,
  Empty,
  ErrorText,
  PageStack,
  PrimaryText,
  Select,
  Table,
  TableScroll,
  Td,
  Th,
  TwoCol,
} from "../../../../components/PartnerDashboard/ui";
import {
  adminGetAllBusiness,
  adminGetBookingsReport,
} from "../../../../api/apiEndpoints";
import { addDays, downloadCsv } from "../../../../utils/reportDates";
import BookingsTable from "../BookingsTable";
import DropConversion from "../DropConversion";
import MultiSelect from "../MultiSelect";
import RangePicker, { useRange } from "../RangePicker";
import { ActionRow, Metric, MetricGrid, MetricLabel, MetricValue } from "../ui";

type BookingType = "regular" | "drop";
type Basis = "booked" | "class";
type Bucket = "day" | "week" | "month";

interface BookingRow {
  day: string;
  businessId: string;
  studio: string | null;
  type: BookingType;
  status: string;
  count: number;
}

interface BookingsReport {
  rows: BookingRow[];
  uniqueMembers: Record<BookingType, number>;
}

const COLORS: Record<BookingType, string> = {
  regular: "#2a78d6",
  drop: "#eb6834",
};
const TYPE_LABEL: Record<BookingType, string> = {
  regular: "Regular bookings",
  drop: "Class drops",
};

const STATUSES: { key: string; label: string }[] = [
  { key: "attended", label: "Attended" },
  { key: "booked", label: "Upcoming / not yet checked" },
  { key: "no_show", label: "No-show" },
  { key: "late_cancelled", label: "Late cancel" },
  { key: "cancelled", label: "Cancelled / excused" },
];

const TipBox = styled.div`
  background: #111111;
  color: #ffffff;
  font-family: "Aileron", "Poppins", sans-serif;
  font-size: 12px;
  padding: 8px 10px;
  border-radius: 8px;
  line-height: 1.6;
`;

const Swatch = styled.span<{ $color: string }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 2px;
  margin-right: 6px;
  background: ${({ $color }) => $color};
`;

const dayDiff = (a: string, b: string) =>
  Math.round(
    (Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86400000,
  );

const autoBucket = (start: string, end: string): Bucket => {
  const days = dayDiff(start, end) + 1;
  if (days <= 62) return "day";
  if (days <= 200) return "week";
  return "month";
};

const bucketKey = (day: string, bucket: Bucket) => {
  if (bucket === "day") return day;
  if (bucket === "month") return `${day.slice(0, 7)}-01`;
  const dow = (new Date(`${day}T00:00:00Z`).getUTCDay() + 6) % 7;
  return addDays(day, -dow);
};

const bucketLabel = (key: string, bucket: Bucket) => {
  const d = new Date(`${key}T00:00:00Z`);
  if (bucket === "month")
    return d.toLocaleDateString("en-US", {
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    });
  const label = d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
  return bucket === "week" ? `Wk of ${label}` : label;
};

const allBuckets = (start: string, end: string, bucket: Bucket) => {
  const keys: string[] = [];
  let cursor = bucketKey(start, bucket);
  while (cursor <= end) {
    keys.push(cursor);
    if (bucket === "day") cursor = addDays(cursor, 1);
    else if (bucket === "week") cursor = addDays(cursor, 7);
    else {
      const [y, m] = cursor.split("-").map(Number);
      cursor = new Date(Date.UTC(y, m, 1)).toISOString().slice(0, 10);
    }
  }
  return keys;
};

const pct = (part: number, whole: number) =>
  whole ? `${((part / whole) * 100).toFixed(1)}%` : "—";

const BookingsPage = () => {
  const range = useRange("last30");
  const [basis, setBasis] = useState<Basis>("booked");
  const [studioIds, setStudioIds] = useState<string[]>([]);
  const [bucketChoice, setBucketChoice] = useState<Bucket | "auto">("auto");
  const [studios, setStudios] = useState<
    { businessId: string; name: string }[]
  >([]);
  const [report, setReport] = useState<BookingsReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    adminGetAllBusiness()
      .then((res) =>
        setStudios(
          ((res.data as any).businesses ?? [])
            .map((b: any, _: number, all: any[]) => {
              const name = b.name || "Unnamed studio";
              const shared = all.filter((o) => (o.name || "Unnamed studio") === name).length > 1;
              const where = b.metro || b.address?.fullAddress || b.businessId.slice(0, 8);
              return { businessId: b.businessId, name: shared ? `${name} · ${where}` : name };
            })
            .sort((a: any, b: any) => a.name.localeCompare(b.name)),
        ),
      )
      .catch(() => setStudios([]));
  }, []);

  useEffect(() => {
    if (range.invalid) return;
    let cancelled = false;
    setReport(null);
    setError(null);
    adminGetBookingsReport({
      start: range.range.start,
      end: range.endExclusive,
      businessIds: studioIds.length ? studioIds.join(",") : undefined,
      basis,
    })
      .then((res) => !cancelled && setReport(res.data))
      .catch(
        () =>
          !cancelled && setError("Couldn't load bookings. Please try again."),
      );
    return () => {
      cancelled = true;
    };
  }, [range.range.start, range.endExclusive, range.invalid, studioIds, basis]);

  const bucket =
    bucketChoice === "auto"
      ? autoBucket(range.range.start, range.range.end)
      : bucketChoice;
  const rows = useMemo(() => report?.rows ?? [], [report]);

  const totals = useMemo(() => {
    const byType = { regular: 0, drop: 0 };
    const byStatus: Record<string, Record<BookingType, number>> = {};
    rows.forEach((r) => {
      byType[r.type] += r.count;
      byStatus[r.status] = byStatus[r.status] ?? { regular: 0, drop: 0 };
      byStatus[r.status][r.type] += r.count;
    });
    return { byType, byStatus, all: byType.regular + byType.drop };
  }, [rows]);

  const series = useMemo(() => {
    if (range.invalid) return [];
    const map = new Map(
      allBuckets(range.range.start, range.range.end, bucket).map((k) => [
        k,
        { key: k, label: bucketLabel(k, bucket), regular: 0, drop: 0 },
      ]),
    );
    rows.forEach((r) => {
      const point = map.get(bucketKey(r.day, bucket));
      if (point) point[r.type] += r.count;
    });
    return Array.from(map.values());
  }, [rows, bucket, range.range.start, range.range.end, range.invalid]);

  const byStudio = useMemo(() => {
    const map = new Map<
      string,
      {
        businessId: string;
        name: string;
        regular: number;
        drop: number;
        attended: number;
        noShow: number;
        lateCancel: number;
      }
    >();
    rows.forEach((r) => {
      const s = map.get(r.businessId) ?? {
        businessId: r.businessId,
        name: r.studio ?? "Unknown studio",
        regular: 0,
        drop: 0,
        attended: 0,
        noShow: 0,
        lateCancel: 0,
      };
      s[r.type] += r.count;
      if (r.status === "attended") s.attended += r.count;
      if (r.status === "no_show") s.noShow += r.count;
      if (r.status === "late_cancelled") s.lateCancel += r.count;
      map.set(r.businessId, s);
    });
    return Array.from(map.values()).sort(
      (a, b) => b.regular + b.drop - (a.regular + a.drop),
    );
  }, [rows]);

  const studioName =
    studioIds.length === 0
      ? "All studios"
      : studioIds.length === 1
      ? studios.find((s) => s.businessId === studioIds[0])?.name ?? "1 studio"
      : `${studioIds.length} studios`;
  const fileSuffix = `${studioName} ${range.range.start} to ${range.range.end}`;

  const downloadSeries = () =>
    downloadCsv(
      [
        [
          bucket === "day" ? "Date" : bucket === "week" ? "Week of" : "Month",
          "Regular bookings",
          "Class drops",
          "Total",
        ],
        ...series.map((p) => [p.key, p.regular, p.drop, p.regular + p.drop]),
      ],
      `Bookings by ${bucket} ${fileSuffix}.csv`,
    );

  const downloadStudios = () =>
    downloadCsv(
      [
        [
          "Studio",
          "Regular bookings",
          "Class drops",
          "Total",
          "Attended",
          "No-shows",
          "Late cancels",
        ],
        ...byStudio.map((s) => [
          s.name,
          s.regular,
          s.drop,
          s.regular + s.drop,
          s.attended,
          s.noShow,
          s.lateCancel,
        ]),
      ],
      `Bookings by studio ${fileSuffix}.csv`,
    );

  const renderTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const p = payload[0].payload;
    return (
      <TipBox>
        <div style={{ fontWeight: 700 }}>{p.label}</div>
        <div>
          <Swatch $color={COLORS.regular} />
          Regular: {p.regular.toLocaleString()}
        </div>
        <div>
          <Swatch $color={COLORS.drop} />
          Class drops: {p.drop.toLocaleString()}
        </div>
        <div>Total: {(p.regular + p.drop).toLocaleString()}</div>
      </TipBox>
    );
  };

  const loading = !report && !error;
  const tiles = [
    { label: "Total bookings", value: totals.all.toLocaleString() },
    {
      label: "Regular bookings",
      value: totals.byType.regular.toLocaleString(),
      color: COLORS.regular,
    },
    {
      label: "Class drops",
      value: totals.byType.drop.toLocaleString(),
      color: COLORS.drop,
    },
    { label: "Class drop share", value: pct(totals.byType.drop, totals.all) },
    {
      label: "Members booking regular",
      value: (report?.uniqueMembers.regular ?? 0).toLocaleString(),
    },
    {
      label: "Members using drops",
      value: (report?.uniqueMembers.drop ?? 0).toLocaleString(),
    },
  ];

  return (
    <PageStack>
      <Panel
        title="Period"
        info={'"Date booking was made" shows when members are booking; "date of the class" shows when they\'re actually going. Days follow Eastern time.'}
        subtitle={`${range.label}${range.label ? " · Eastern time" : ""}`}
        action={
          <ActionRow>
            <div style={{ width: 260, maxWidth: "100%" }}>
              <MultiSelect
                options={studios.map((s) => ({ value: s.businessId, label: s.name }))}
                selected={studioIds}
                onChange={setStudioIds}
                allLabel="All studios"
                noun="studios"
              />
            </div>
            <Select
              value={basis}
              onChange={(e) => setBasis(e.target.value as Basis)}
              aria-label="Count bookings by"
            >
              <option value="booked">Date booking was made</option>
              <option value="class">Date of the class</option>
            </Select>
          </ActionRow>
        }
      >
        <RangePicker state={range} />
        {error && <ErrorText>{error}</ErrorText>}
      </Panel>

      {!error && (
        <>
          <MetricGrid>
            {tiles.map((t) => (
              <Metric key={t.label}>
                {loading ? (
                  <Skeleton $w="50%" $h="24px" $radius="8px" />
                ) : (
                  <MetricValue>{report ? t.value : "—"}</MetricValue>
                )}
                <MetricLabel>
                  {t.color && <Swatch $color={t.color} />}
                  {t.label}
                </MetricLabel>
              </Metric>
            ))}
          </MetricGrid>

          <Panel
            title="Bookings over time"
            info="Every booking made through JackedRabbit, whatever happened to it afterwards. Class drop attempts that never went through are left out."
            action={
              <ActionRow>
                <Chips>
                  {(["auto", "day", "week", "month"] as const).map((b) => (
                    <Chip
                      key={b}
                      $active={bucketChoice === b}
                      onClick={() => setBucketChoice(b)}
                    >
                      {b === "auto" ? "Auto" : `By ${b}`}
                    </Chip>
                  ))}
                </Chips>
                <Button disabled={!report} onClick={downloadSeries}>
                  <Download size={15} />
                  CSV
                </Button>
              </ActionRow>
            }
          >
            {loading ? (
              <Skeleton $h="280px" $radius="12px" />
            ) : totals.all === 0 ? (
              <Empty>No bookings in this period.</Empty>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart
                  data={series}
                  margin={{ top: 6, right: 6, left: 0, bottom: 0 }}
                  barCategoryGap="20%"
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#f0ebe4"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11, fill: "#9a8e84" }}
                    axisLine={{ stroke: "#e7e0d8" }}
                    tickLine={false}
                    minTickGap={12}
                  />
                  <YAxis
                    width={36}
                    tick={{ fontSize: 11, fill: "#9a8e84" }}
                    axisLine={false}
                    tickLine={false}
                    allowDecimals={false}
                  />
                  <Tooltip
                    content={renderTooltip}
                    cursor={{ fill: "rgba(0,0,0,0.04)" }}
                  />
                  <Legend
                    verticalAlign="top"
                    align="right"
                    iconType="square"
                    iconSize={9}
                    wrapperStyle={{
                      fontSize: 12,
                      color: "#4a423b",
                      paddingBottom: 8,
                    }}
                    formatter={(value: string) => (
                      <span style={{ color: "#4a423b" }}>
                        {TYPE_LABEL[value as BookingType]}
                      </span>
                    )}
                  />
                  <Bar
                    dataKey="regular"
                    stackId="b"
                    fill={COLORS.regular}
                    stroke="#ffffff"
                    strokeWidth={1}
                  />
                  <Bar
                    dataKey="drop"
                    stackId="b"
                    fill={COLORS.drop}
                    stroke="#ffffff"
                    strokeWidth={1}
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Panel>

          <TwoCol>
            <Panel
              title="What happened to them"
              info="Excused no-shows are moved to cancelled, so no-shows and late cancels here are unexcused."
            >
              {loading ? (
                <Skeleton $h="180px" $radius="12px" />
              ) : (
                <TableScroll>
                  <Table>
                    <thead>
                      <tr>
                        <Th>Status</Th>
                        <Th $align="right">Regular</Th>
                        <Th $align="right">Class drops</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {STATUSES.map((s) => {
                        const c = totals.byStatus[s.key] ?? {
                          regular: 0,
                          drop: 0,
                        };
                        return (
                          <tr key={s.key}>
                            <Td>{s.label}</Td>
                            <Td data-label="Regular" $align="right">
                              <span>
                                {c.regular.toLocaleString()}{" "}
                                <span style={{ color: "#9a8e84" }}>
                                  ({pct(c.regular, totals.byType.regular)})
                                </span>
                              </span>
                            </Td>
                            <Td data-label="Class drops" $align="right">
                              <span>
                                {c.drop.toLocaleString()}{" "}
                                <span style={{ color: "#9a8e84" }}>
                                  ({pct(c.drop, totals.byType.drop)})
                                </span>
                              </span>
                            </Td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </TableScroll>
              )}
            </Panel>

            <Panel
              title="By studio"
              subtitle={
                studioIds.length === 1 ? undefined : "Click a studio to filter the page to it"
              }
              action={
                <Button disabled={!report} onClick={downloadStudios}>
                  <Download size={15} />
                  CSV
                </Button>
              }
            >
              {loading ? (
                <Skeleton $h="180px" $radius="12px" />
              ) : byStudio.length === 0 ? (
                <Empty>No bookings in this period.</Empty>
              ) : (
                <TableScroll style={{ maxHeight: 420, overflowY: "auto" }}>
                  <Table>
                    <thead>
                      <tr>
                        <Th>Studio</Th>
                        <Th $align="right">Regular</Th>
                        <Th $align="right">Drops</Th>
                        <Th $align="right">Total</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {byStudio.map((s) => (
                        <tr
                          key={s.businessId}
                          style={{ cursor: "pointer" }}
                          onClick={() => setStudioIds([s.businessId])}
                        >
                          <Td>
                            <PrimaryText>{s.name}</PrimaryText>
                          </Td>
                          <Td data-label="Regular" $align="right">
                            {s.regular.toLocaleString()}
                          </Td>
                          <Td data-label="Drops" $align="right">
                            {s.drop.toLocaleString()}
                          </Td>
                          <Td data-label="Total" $align="right">
                            <PrimaryText>
                              {(s.regular + s.drop).toLocaleString()}
                            </PrimaryText>
                          </Td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </TableScroll>
              )}
            </Panel>
          </TwoCol>

          <DropConversion
            start={range.range.start}
            end={range.endExclusive}
            businessIds={studioIds.length ? studioIds.join(",") : undefined}
            basis={basis}
            invalid={range.invalid}
            fileSuffix={fileSuffix}
          />

          <BookingsTable
            start={range.range.start}
            end={range.endExclusive}
            businessIds={studioIds.length ? studioIds.join(",") : undefined}
            basis={basis}
            invalid={range.invalid}
            fileSuffix={fileSuffix}
          />
        </>
      )}
    </PageStack>
  );
};

export default BookingsPage;
